<template>
  <div ref="drop-zone" class="position-relative">
    <v-card
      v-if="isOverDropZone"
      variant="tonal"
      class="d-flex flex-column w-full h-full position-absolute bg-surface"
      style="z-index: 1; inset: 0; border: 2px dashed rgb(var(--v-theme-primary)); border-radius: 8px"
    >
      <v-card-text class="h-full d-flex justify-center align-center">
        <div class="ma-auto text-body-medium text-center" variant="text">
          <v-card-item class="text-body-medium">
            <v-icon class="text-medium-emphasis" size="62" icon="upload_file" />
            <div class="mt-2 text-title-large">{{ $t("uploadFiles") }}</div>
          </v-card-item>
        </div>
      </v-card-text>
    </v-card>

    <v-form ref="uploadForm" v-model="valid" :readonly="uploading" @submit.prevent>
      <v-card-text>
        <v-file-input
          v-model="files"
          :accept="acceptValidation"
          :disabled="uploading"
          :hint="`${$t('maxFiles', { max: maxFiles })}, ${$t('maxFileSize', { max: prettyBytes(maxFileSize) })}`"
          :label="$t('pickFiles')"
          :multiple="multiple"
          :rules="rules"
          chips
          clearable
          persistent-hint
          single-line
          @update:model-value="uploadFiles"
        >
          <template #selection="{ fileNames }">{{ $t("uploading") }} ({{ fileNames.length }})</template>
        </v-file-input>

        <div v-show="uploadedFiles.length || files?.length">
          <v-divider class="my-2" />
          <v-list-subheader>{{ $t("uploadedFiles") }} ({{ uploadedFiles.length }})</v-list-subheader>

          <!-- combining already uploaded files (FileData) and to-be-uploaded files (File) in one array for smooth transitions -->
          <transition-group name="slide-x-reverse-transition" tag="v-list" class="overflow-y-auto" max-height="300">
            <v-list-item
              v-for="file in [...(files ?? []), ...uploadedFiles]"
              :key="'name' in file ? file.name : `${file.description}.${file.extension}`"
            >
              <template #prepend>
                <v-progress-circular v-if="uploading && 'name' in file" class="mr-4" indeterminate width="3" color="primary" />
                <v-avatar v-if="!uploading && 'name' in file" variant="tonal" icon="error" size="small" color="error" />
                <v-avatar v-else variant="tonal" icon="check" size="small" color="success" />
              </template>

              <template #default>
                <span v-if="'name' in file">{{ file.name }}</span>
                <span v-else>{{ file.description }}.{{ file.extension }}</span>
              </template>

              <template #subtitle>
                <span class="text-body-small">{{ prettyBytes(file.size) }}</span>
              </template>

              <template v-if="canDelete && 'description' in file" #append>
                <v-btn icon="delete" density="comfortable" color="error" :disabled="deleting" rounded @click="() => onDelete(file._id)" />
              </template>
            </v-list-item>
          </transition-group>
        </div>
      </v-card-text>

      <v-card-actions class="justify-end">
        <v-btn v-if="!uploading && !uploadedFiles.length" color="secondary" class="custom-hover-secondary" @click="onCancel">{{
          $t("cancel")
        }}</v-btn>
        <v-btn
          variant="flat"
          color="primary"
          class="custom-hover-primary"
          type="submit"
          :disabled="!uploadedFiles.length"
          :loading="uploading"
          @click="onCancel"
          >{{ $t("save") }}
        </v-btn>
      </v-card-actions>
    </v-form>
  </div>
</template>

<script setup lang="ts">
import prettyBytes from "pretty-bytes"

const $i18n = useI18n()
const config = useRuntimeConfig()

const mainStore = useMainStore()
const authStore = useAuthStore()
const { currentOrganization } = storeToRefs(authStore)

const uploadForm = useTemplateRef("uploadForm")

const emit = defineEmits<{
  (event: "upload", value: Array<FileData>): void
  (event: "delete", fileId: string): void
  (event: "ok"): void
}>()

const {
  accept = undefined,
  multiple = false,
  createFunction = undefined,
  deleteFunction = undefined,
} = defineProps<{
  accept?: string
  multiple?: boolean
  createFunction?: (files: Array<File>) => Promise<Array<FileData>>
  deleteFunction?: (fileId: string) => Promise<unknown>
}>()

const dropZoneRef = useTemplateRef("drop-zone")

const onDrop = async (droppedFiles: Array<File> | null) => {
  if (!droppedFiles) {
    return
  }

  files.value = droppedFiles
  return uploadFiles()
}

const { isOverDropZone } = useDropZone(dropZoneRef, {
  onDrop,
  // specify the types of data to be received.
  dataTypes: accept?.includes("*") ? [] : accept?.replace(" ", "").split(","),
  // control multi-file drop
  multiple: true,
  // whether to prevent default behavior for unhandled events
  preventDefaultForUnhandled: false,
})

const uploadedFiles = ref<Array<FileData>>([])
const files = ref<Array<File> | undefined>()
const uploading = ref(false)
const deleting = ref(false)
const valid = ref(false)

const { validateFilesUpload } = useFileUploadValidation()

const canDelete = computed(() => !!deleteFunction && authStore.hasScope(currentOrganization.value!, AuthScope.CAN_DELETE_FILES))

const maxFileSize = Number(config.public.MAX_FILE_SIZE_IN_BYTES)
const maxFiles = Number(config.public.MAX_FILES_IN_BATCH)

const rules = computed(() => {
  const isFileTypeValid = (fileType: string) => {
    const mime = accept
    if (!mime) {
      return true
    }

    if (mime.endsWith("*")) {
      return fileType.startsWith(mime.replace("*", ""))
    }

    return mime.includes(fileType)
  }

  const mimeMatchesAcceptRule = [(val: File[]) => (val && val.every((file: File) => isFileTypeValid(file.type))) || $i18n.t("invalidFile")]

  return [...mimeMatchesAcceptRule, (files: File[]) => validateFilesUpload(files)]
})

const acceptValidation = computed(() => {
  // If accept value is image/* we should accept all images.
  // However, Android 12+ shows the file browser instead of the camera/browser option.
  // When we remove the accept and verify this manually Android does show the camera or browser choice.
  // https://support.google.com/chrome/thread/253908853/android-14-camera-tile-removed-from-photo-picker?hl=en
  if (!accept || accept.endsWith("*")) {
    return undefined
  }
  return accept
})

const uploadFiles = async () => {
  const { valid: isFormValid } = (await uploadForm.value?.validate()) ?? { valid: false }

  if (!isFormValid || !files.value?.length) {
    return
  }

  useExplicitSave(mainStore.notify, $i18n, async () => {
    uploading.value = true

    try {
      if (!createFunction) {
        throw new Error("No create function provided")
      }

      const newFiles = await createFunction(files.value!)

      emit("upload", newFiles)
      uploadedFiles.value = [...newFiles, ...uploadedFiles.value]
      files.value = undefined
    } catch (e) {
      const errorMessage = getErrorMessage(e)
      console.error(errorMessage)
      throw e
    } finally {
      uploading.value = false
    }
  })
}

const onDelete = (fileId: string) => {
  if (!deleteFunction) {
    throw new Error("No delete function provided")
  }

  deleting.value = true

  useExplicitSave(
    mainStore.notify,
    $i18n,
    async () => {
      await deleteFunction(fileId)
      uploadedFiles.value = uploadedFiles.value.filter((file) => file._id !== fileId)
      emit("delete", fileId)
    },
    "remove",
  ).finally(() => {
    deleting.value = false
  })
}

const onCancel = () => {
  emit("ok")
}
</script>
